import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, X, ChevronLeft, ChevronRight } from 'lucide-react';
import './ValidUntilPicker.scss';

const DAYS_SK   = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne'];
const MONTHS_SK = [
  'Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún',
  'Júl', 'August', 'September', 'Október', 'November', 'December',
];

// ─── helpers ──────────────────────────────────────────────────────────────────

function toISODate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDateSK(dateStr) {
  const d = new Date(dateStr);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

function getSundayOfWeek(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay(); // 0 = Sunday
  const diff = day === 0 ? 0 : 7 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function buildDaysGrid(year, month) {
  const firstDay    = new Date(year, month, 1);
  const lastDay     = new Date(year, month + 1, 0);
  const startDow    = (firstDay.getDay() + 6) % 7; // Mon = 0
  const result      = [];

  for (let i = startDow - 1; i >= 0; i--) {
    result.push({ date: new Date(year, month, -i), outside: true });
  }
  for (let i = 1; i <= lastDay.getDate(); i++) {
    result.push({ date: new Date(year, month, i), outside: false });
  }
  const remaining = 42 - result.length;
  for (let i = 1; i <= remaining; i++) {
    result.push({ date: new Date(year, month + 1, i), outside: true });
  }
  return result;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ValidUntilPicker
 *
 * Props:
 *   value       – YYYY-MM-DD string (Sunday) or null  → currently saved valid_until
 *   onChange    – (YYYY-MM-DD | null) → void           → called with Sunday ISO date or null
 *   modalSunday – YYYY-MM-DD string                    → Sunday of the currently edited modal week
 */
function ValidUntilPicker({ value, onChange, modalSunday }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const base = value ? new Date(value) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

  const wrapperRef  = useRef(null); // the .vup root div (trigger side)
  const popoverRef  = useRef(null); // the portal popover div

  // Normalised today (midnight)
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const todayStr = toISODate(today);

  // Normalised selected Sunday
  const selectedSunday = useMemo(() => {
    if (!value) return null;
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [value]);
  const selectedStr = selectedSunday ? toISODate(selectedSunday) : null;

  // Close on outside click – must cover both the wrapper AND the portal popover
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      const inWrapper  = wrapperRef.current  && wrapperRef.current.contains(e.target);
      const inPopover  = popoverRef.current  && popoverRef.current.contains(e.target);
      if (!inWrapper && !inPopover) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // ── Popover positioning ──────────────────────────────────────────────────────

  function computePosition() {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    const POPOVER_WIDTH  = 260;
    const POPOVER_HEIGHT = 345; // conservative upper-bound estimate
    const GAP            = 6;

    // ── Horizontal: flip to right-align if overflowing right edge ────────────
    let left = rect.left;
    if (left + POPOVER_WIDTH > window.innerWidth - 8) {
      left = rect.right - POPOVER_WIDTH;
      if (left < 8) left = 8;
    }

    // ── Vertical: open upward if not enough space below ───────────────────────
    const spaceBelow = window.innerHeight - rect.bottom;
    let top;
    if (spaceBelow < POPOVER_HEIGHT + GAP) {
      // Flip above the trigger
      top = rect.top - POPOVER_HEIGHT - GAP;
      if (top < 8) top = 8; // clamp to viewport top
    } else {
      top = rect.bottom + GAP;
    }

    setPopoverPos({ top, left });
  }

  // Compute once as soon as the popover should appear (before paint to avoid flicker)
  useLayoutEffect(() => {
    if (isOpen) computePosition();
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute on scroll / resize while open
  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', computePosition, true);
    window.addEventListener('resize', computePosition);
    return () => {
      window.removeEventListener('scroll', computePosition, true);
      window.removeEventListener('resize', computePosition);
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const days = useMemo(() => buildDaysGrid(year, month), [year, month]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleDayClick(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    if (d < today) return; // past – should not be reachable (disabled)
    const sunday = getSundayOfWeek(d);
    onChange(toISODate(sunday));
    // Navigate to the month that contains the chosen Sunday (for visual feedback)
    setViewDate(new Date(sunday.getFullYear(), sunday.getMonth(), 1));
    setIsOpen(false);
  }

  function handleClear(e) {
    e && e.stopPropagation();
    onChange(null);
    setIsOpen(false);
  }

  function handleThisWeek() {
    if (!modalSunday) return;
    onChange(modalSunday);
    const d = new Date(modalSunday);
    setViewDate(new Date(d.getFullYear(), d.getMonth(), 1));
    setIsOpen(false);
  }

  function prevMonth() { setViewDate(new Date(year, month - 1, 1)); }
  function nextMonth() { setViewDate(new Date(year, month + 1, 1)); }

  // ── Day class logic ──────────────────────────────────────────────────────────

  function getDayClass(date, outside) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const iso     = toISODate(d);
    const isPast  = d < today;
    const inRange = selectedSunday && d >= today && d <= selectedSunday;

    let cls = 'vup__day';
    if (outside) { cls += ' vup__day--outside'; return cls; }
    if (isPast)    cls += ' vup__day--past';

    if (inRange) {
      const isStart = iso === todayStr;
      const isEnd   = iso === selectedStr;
      if (isStart && isEnd)   cls += ' vup__day--range-single';
      else if (isStart)       cls += ' vup__day--range-start';
      else if (isEnd)         cls += ' vup__day--range-end';
      else                    cls += ' vup__day--range-mid';
    } else if (!isPast && iso === todayStr) {
      cls += ' vup__day--today';
    }

    return cls;
  }

  // ── Display text ─────────────────────────────────────────────────────────────

  const displayText = value
    ? `Do ${formatDateSK(value)}`
    : 'Platné navždy';

  // ── Render ───────────────────────────────────────────────────────────────────

  const popoverNode = (
    <div
      className="vup__popover"
      ref={popoverRef}
      style={{ top: popoverPos.top, left: popoverPos.left }}
    >

      {/* Quick action */}
      {modalSunday && (
        <div className="vup__quick">
          <button type="button" className="vup__quick-btn" onClick={handleThisWeek}>
            Platné iba tento týždeň
          </button>
        </div>
      )}

      {/* Month navigation */}
      <div className="vup__header">
        <button type="button" className="vup__nav-btn" onClick={prevMonth} aria-label="Predchádzajúci mesiac">
          <ChevronLeft size={14} />
        </button>
        <span className="vup__month-label">{MONTHS_SK[month]} {year}</span>
        <button type="button" className="vup__nav-btn" onClick={nextMonth} aria-label="Nasledujúci mesiac">
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="vup__weekdays">
        {DAYS_SK.map((d) => (
          <span key={d} className="vup__weekday">{d}</span>
        ))}
      </div>

      {/* Days grid */}
      <div className="vup__days">
        {days.map(({ date, outside }, i) => {
          const d = new Date(date);
          d.setHours(0, 0, 0, 0);
          const isPast = d < today;
          return (
            <button
              key={i}
              type="button"
              className={getDayClass(date, outside)}
              onClick={() => !outside && !isPast && handleDayClick(date)}
              disabled={outside || isPast}
              tabIndex={outside ? -1 : 0}
              aria-label={toISODate(d)}
            >
              <span className="vup__day-inner">{date.getDate()}</span>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="vup__footer">
        <button type="button" className="vup__footer-clear" onClick={handleClear}>
          Platné navždy (zrušiť)
        </button>
      </div>
    </div>
  );

  return (
    <div className="vup" ref={wrapperRef}>
      {/* Trigger */}
      <button
        type="button"
        className={`vup__trigger${value ? ' vup__trigger--set' : ''}`}
        onClick={() => setIsOpen((v) => !v)}
        title={value ? `Platné do ${formatDateSK(value)}` : 'Klikni pre nastavenie platnosti'}
      >
        <CalendarDays size={11} className="vup__trigger-icon" />
        <span className="vup__trigger-text">{displayText}</span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            className="vup__trigger-clear"
            onClick={handleClear}
            onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
            aria-label="Zrušiť platnosť"
          >
            <X size={9} />
          </span>
        )}
      </button>

      {/* Popover rendered via portal → escapes all overflow:hidden parents */}
      {isOpen && createPortal(popoverNode, document.body)}
    </div>
  );
}

export default ValidUntilPicker;
